import { useState, useEffect, useRef } from 'react';
import { buildNextStepMessage, buildIdleTimeoutMessage } from '../utils/conversationFlow';
import { logBotMessage } from '../utils/api';

const IDLE_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
const IDLE_AUTOCLOSE_MS = 2 * 60 * 1000; // 2 minutes
export const DEFERRED_MESSAGE_DELAY = 30 * 1000; // 30 seconds
const VIDEO_POLL_INTERVAL = 5 * 1000; // 5 seconds

const useIdleTimeout = ({
  isOpen, state, dispatch, onSessionEnd, communityNid, communityName,
}) => {
  const { isSessionEnded, conversationState, sessionId } = state;

  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [isIdle, setIsIdle] = useState(false);
  const idleTimeoutRef = useRef(null);
  const idleAutoCloseRef = useRef(null);
  const deferredMessageRef = useRef(null);
  const deferredCallbackRef = useRef(null);
  const isSessionEndedRef = useRef(isSessionEnded);
  isSessionEndedRef.current = isSessionEnded;

  useEffect(() => {
    const hasCompletedForm = !!(
      conversationState?.userInfo?.firstName
      && conversationState?.userInfo?.lastName
      && conversationState?.userInfo?.email
    );

    if (!isOpen || isSessionEnded || isIdle || !hasCompletedForm) {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = null;
      }
      if (idleAutoCloseRef.current) {
        clearTimeout(idleAutoCloseRef.current);
        idleAutoCloseRef.current = null;
      }
      return undefined;
    }

    const checkIdle = () => {
      const timeSinceActivity = Date.now() - lastActivityTime;
      if (timeSinceActivity >= IDLE_TIMEOUT_MS) {
        const idleMessage = buildIdleTimeoutMessage(conversationState?.userInfo?.firstName || '', { communityName });
        dispatch({ type: 'APPEND_MESSAGES', payload: [idleMessage], deferred: true });
        setIsIdle(true);

        if (idleAutoCloseRef.current) {
          clearTimeout(idleAutoCloseRef.current);
        }
        idleAutoCloseRef.current = setTimeout(() => {
          if (!isSessionEndedRef.current) {
            onSessionEnd();
          }
        }, IDLE_AUTOCLOSE_MS);
      } else {
        const remainingTime = IDLE_TIMEOUT_MS - timeSinceActivity;
        idleTimeoutRef.current = setTimeout(checkIdle, remainingTime + 1000);
      }
    };

    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    idleTimeoutRef.current = setTimeout(checkIdle, IDLE_TIMEOUT_MS);

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      if (idleAutoCloseRef.current) {
        clearTimeout(idleAutoCloseRef.current);
      }
    };
  }, [isOpen, lastActivityTime, isSessionEnded, isIdle, conversationState.userInfo]);

  // Clear refs before invoking the callback so a chained
  // scheduleDeferredMessage call inside cb() isn't overwritten when this
  // function returns.
  const fireDeferredCallback = () => {
    const cb = deferredCallbackRef.current;
    deferredCallbackRef.current = null;
    deferredMessageRef.current = null;
    dispatch({ type: 'UPDATE_LAST_MESSAGE', payload: { nextDelayDeadline: 0 } });
    if (cb) cb();
  };

  const resetDeferredTimer = () => {
    if (deferredMessageRef.current && deferredCallbackRef.current) {
      clearTimeout(deferredMessageRef.current);
      deferredMessageRef.current = setTimeout(fireDeferredCallback, DEFERRED_MESSAGE_DELAY);
      dispatch({
        type: 'UPDATE_LAST_MESSAGE',
        payload: {
          nextDelayMs: DEFERRED_MESSAGE_DELAY,
          nextDelayDeadline: Date.now() + DEFERRED_MESSAGE_DELAY,
        },
      });
    }
  };

  const scheduleDeferredMessage = (callback, delay = DEFERRED_MESSAGE_DELAY) => {
    if (deferredMessageRef.current) {
      clearTimeout(deferredMessageRef.current);
    }
    deferredCallbackRef.current = callback;
    deferredMessageRef.current = setTimeout(fireDeferredCallback, delay);
    dispatch({
      type: 'UPDATE_LAST_MESSAGE',
      payload: { nextDelayMs: delay, nextDelayDeadline: Date.now() + delay },
    });
  };

  const flushDeferredMessage = () => {
    if (deferredMessageRef.current && deferredCallbackRef.current) {
      clearTimeout(deferredMessageRef.current);
      fireDeferredCallback();
      return true;
    }
    return false;
  };

  const cancelDeferredMessage = () => {
    if (deferredMessageRef.current) {
      clearTimeout(deferredMessageRef.current);
      deferredMessageRef.current = null;
      deferredCallbackRef.current = null;
    }
  };

  // Reset deferred message timer on any window activity.
  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleWindowActivity = () => resetDeferredTimer();

    events.forEach((event) => window.addEventListener(event, handleWindowActivity));
    return () => {
      events.forEach((event) => window.removeEventListener(event, handleWindowActivity));
    };
  }, []);

  // Treat an open map modal as ongoing activity so the idle timer doesn't
  // fire while the user is reading a place detail.
  useEffect(() => {
    const checkModal = () => {
      if (document.body.classList.contains('modalOpen')) {
        setLastActivityTime(Date.now());
        resetDeferredTimer();
      }
    };

    const interval = setInterval(checkModal, VIDEO_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const recordActivity = () => {
    setLastActivityTime(Date.now());
    if (isIdle) {
      setIsIdle(false);
    }
    if (idleAutoCloseRef.current) {
      clearTimeout(idleAutoCloseRef.current);
      idleAutoCloseRef.current = null;
    }
    resetDeferredTimer();
  };

  const handleIdleTimeoutYes = () => {
    setIsIdle(false);
    if (idleAutoCloseRef.current) {
      clearTimeout(idleAutoCloseRef.current);
      idleAutoCloseRef.current = null;
    }
    onSessionEnd();
  };

  const handleIdleTimeoutNo = () => {
    const firstName = conversationState?.userInfo?.firstName || '';
    const msg = buildNextStepMessage(firstName, { communityName });
    if (msg) {
      dispatch({ type: 'APPEND_MESSAGES', payload: [msg] });
      dispatch({ type: 'SET_FLOW_STEP', payload: 7 });
      logBotMessage(msg.text, {
        sessionId,
        selectedInterests: conversationState.selectedInterests?.map((i) => i.uuid) || [],
        userInfo: conversationState.userInfo,
        communityNid,
        currentStep: 'next_step',
        questionType: 'next_step_prompt',
      });
    }

    setIsIdle(false);
    if (idleAutoCloseRef.current) {
      clearTimeout(idleAutoCloseRef.current);
      idleAutoCloseRef.current = null;
    }
    recordActivity();
  };

  return {
    recordActivity,
    scheduleDeferredMessage,
    cancelDeferredMessage,
    flushDeferredMessage,
    handleIdleTimeoutYes,
    handleIdleTimeoutNo,
    isIdle,
  };
};

export default useIdleTimeout;
